import React, { useEffect, useState } from 'react';
import { DataGrid, trTR, enUS } from '@mui/x-data-grid';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { useTranslate, useLocaleState } from 'react-admin';
import { db } from '../../database/firebase';
import { getSales } from '../../services/salesService';
import { useApiData } from '../../services/apiClient';

const SEATED_COLUMNS = [
  {
    field: 'id',
    headerName: 'Product ID',
    width: 150,
    filterable: false,
    valueGetter: (params) => params.row.id,
  },
  {
    field: 'name',
    headerName: 'Product Name',
    width: 200,
    filterable: false,
    valueGetter: (params) => params.row.name,
  },
  {
    field: 'count',
    headerName: 'Sold',
    width: 100,
    filterable: false,
    valueGetter: (params) => (params.row.count ? params.row.count : 0),
  },
  {
    field: 'total',
    headerName: 'Total',
    width: 100,
    filterable: false,
    valueGetter: (params) => params.row.total,
    valueFormatter: (params) =>
      params.value ? `${params.value} TL` : '0 TL',
  },
  {
    field: 'remaining',
    headerName: 'Remaining',
    width: 100,
    filterable: false,
    valueGetter: (params) => (params.value ? params.row.remaining : 0),
    valueFormatter: (params) =>
      params.value ? `${params.value} TL` : '0 TL',
  },
];

const ORGANISATION_OR_VENUE_SALE_CHANNELS = ['vm', 'vp', 'ocm', 'op'];

const matchesStock = (sale, stock) => {
  const saleStockId = sale?.event?.stockID;
  if (!saleStockId) return false;
  return (
    saleStockId === stock?.stockID ||
    saleStockId === stock?.id ||
    String(saleStockId) === String(stock?.stockID)
  );
};

const SessionTimeAndAvailability = ({ event, stock }) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!event || !stock) {
      setError(true);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(false);

        let docs = [];

        if (useApiData) {
          const rows = await getSales({ eventID: event.id });
          docs = rows.filter((sale) => {
            if (!matchesStock(sale, stock)) return false;
            if (sale.status === 1) return true;
            if (sale.status === 0 && sale.isReservation) return true;
            return false;
          });
        } else {
          const collectionRef = collection(db, 'sales');
          const q = query(
            collectionRef,
            orderBy('createdate', 'desc'),
            where('event.id', '==', event.id),
          );
          const dataSnapshot = await getDocs(q);

          dataSnapshot.forEach((docSnap) => {
            const sale = docSnap.data();
            if (!sale.event?.stockID) return;
            if (matchesStock(sale, stock)) {
              if (sale.status === 1) {
                docs.push({ ...sale, id: docSnap.id });
              } else if (sale.status === 0 && sale.isReservation) {
                docs.push({ ...sale, id: docSnap.id });
              }
            }
          });
        }

        if (!cancelled) {
          setData(docs);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading seated sales:', err);
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [event?.id, stock?.id, stock?.stockID]);

  if (loading) return <div>...</div>;
  if (error) return <div>-</div>;

  let tableData = {};

  if (data) {
    Object.values(data).forEach((sale) => {
      if (typeof sale === 'object' && sale.seats) {
        sale.seats.forEach((seat) => {
          if (!tableData[sale.event.stockID]) {
            tableData[sale.event.stockID] = {};
          }

          if (sale.isReservation && sale.status === 0) {
            if (!tableData[sale.event.stockID][`${seat.productID}-RES`]) {
              let product = stock.stock.find((r) => r.id === seat.productID);

              tableData[sale.event.stockID][`${seat.productID}-RES`] = {
                name: (product?.title?.en || seat.productID) + ' (RESERVATION)',
                count: 1,
                total: 0,
                remaining: product?.price || 0,
              };
            } else {
              let product = stock.stock.find((r) => r.id === seat.productID);
              tableData[sale.event.stockID][`${seat.productID}-RES`].count =
                tableData[sale.event.stockID][`${seat.productID}-RES`].count + 1;
              tableData[sale.event.stockID][`${seat.productID}-RES`].total =
                tableData[sale.event.stockID][`${seat.productID}-RES`].total + 0;
              tableData[sale.event.stockID][`${seat.productID}-RES`].remaining =
                tableData[sale.event.stockID][`${seat.productID}-RES`].remaining + (product?.price || 0);
            }
          } else if (sale.isInvitation) {
            if (!tableData[sale.event.stockID][`${seat.productID}-INV`]) {
              let product = {
                title: { en: seat.selectedOption?.title?.en || '' },
                price: seat.selectedOption?.price || 0,
              };

              tableData[sale.event.stockID][`${seat.productID}-INV`] = {
                name: (product.title.en || seat.productID) + ' (INVITATION)',
                count: 1,
                total: 0,
              };
            } else {
              tableData[sale.event.stockID][`${seat.productID}-INV`].count =
                tableData[sale.event.stockID][`${seat.productID}-INV`].count + 1;
              tableData[sale.event.stockID][`${seat.productID}-INV`].total =
                tableData[sale.event.stockID][`${seat.productID}-INV`].total + 0;
            }
          } else {
            if (!tableData[sale.event.stockID][seat.productID]) {
              let stockProduct = stock.stock.find((r) => r.id === seat.productID);

              let product = {
                title: { en: seat.selectedOption?.title?.en || '' },
                price: seat.selectedOption?.price || 0,
              };
              tableData[sale.event.stockID][seat.productID] = {
                name: (product.title.en || seat.productID) + ' (PRODUCT)',
                count: 1,
                total: ORGANISATION_OR_VENUE_SALE_CHANNELS.includes(sale.channel)
                  ? (stockProduct?.price || 0)
                  : product.price,
              };
            } else {
              let stockProduct = stock.stock.find((r) => r.id === seat.productID);

              let product = {
                title: { en: seat.selectedOption?.title?.en || '' },
                price: seat.selectedOption?.price || 0,
              };

              tableData[sale.event.stockID][seat.productID].count =
                tableData[sale.event.stockID][seat.productID].count + 1;
              tableData[sale.event.stockID][seat.productID].total =
                tableData[sale.event.stockID][seat.productID].total +
                (ORGANISATION_OR_VENUE_SALE_CHANNELS.includes(sale.channel)
                  ? (stockProduct?.price || 0)
                  : product.price);
            }
          }
        });
      }
    });
  }

  let tableDataGrid = [];
  Object.keys(tableData).forEach((sessionEpoch) => {
    Object.keys(tableData[sessionEpoch]).forEach((productID) => {
      tableDataGrid.push({
        id: productID,
        name: tableData[sessionEpoch][productID].name,
        count: tableData[sessionEpoch][productID].count,
        total: tableData[sessionEpoch][productID].total,
        remaining: tableData[sessionEpoch][productID].remaining,
      });
    });
  });

  tableDataGrid.sort((a, b) => b.count - a.count);

  const translatedColumns = SEATED_COLUMNS.map((c) => {
    const colKey = c.field === 'name' ? 'col_title' : c.field === 'count' ? 'col_sold' : c.field === 'remaining' ? 'col_totalRemaining' : `col_${c.field}`;
    return {
      ...c,
      headerName: translate(`report.${colKey}`, { _: c.headerName }),
    };
  });

  const localeText = locale === 'en'
    ? enUS.components.MuiDataGrid.defaultProps.localeText
    : trTR.components.MuiDataGrid.defaultProps.localeText;

  return (
    <div style={{ borderBottom: '2px solid #AAA', margin: 30, marginTop: 0 }}>
      <h3>{translate('report.sales', { _: 'Satışlar' })}</h3>
      <DataGrid
        rows={tableDataGrid}
        density="compact"
        localeText={localeText}
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        hideFooter={true}
        style={{ flex: 1, width: '100%' }}
        getRowId={(row) => row.id}
        columns={translatedColumns}
      />
    </div>
  );
};

export default SessionTimeAndAvailability;
